import React from 'react';
import {
  BookmarkIcon as BookmarkSolidIcon,
  BookmarkIcon as BookmarkOutlineIcon,
  StarIcon,
} from '@heroicons/react/24/solid';
import { Link } from 'react-router-dom';
import { Listing } from './PropertyCardTypes';
import { formatPrice } from '../../../utility/formatPrice';
import { capitalizeWords } from '../../../utility/capitalizeWords';
import { MapPin } from 'lucide-react';
import { useAnalyticsWithConsent } from '../../../hooks/useAnalyticsWithConsent';

interface CompactPropertyCardProps {
  listing: Listing;
  isBookmarked: boolean;
  onBookmarkToggle: (id: number) => void;
  isTopViewed?: boolean;
}

/**
 * Compact property card optimized for mobile devices
 * Inspired by Airbnb's mobile card design - shows essential info in a smaller format
 */
const CompactPropertyCard: React.FC<CompactPropertyCardProps> = ({
  listing,
  isBookmarked,
  onBookmarkToggle,
  isTopViewed = false,
}) => {
  const analytics = useAnalyticsWithConsent();

  return (
    <Link
      to={`/property/${listing.id}`}
      onClick={() => {
        analytics.trackPixelCustom('property_card_click', {
          property_id: listing.id,
          is_top_viewed: isTopViewed,
          card_type: 'compact_mobile',
        });
      }}
      className="block bg-white rounded-xl overflow-hidden no-underline"
    >
      {/* Image with bookmark button */}
      <div className="relative">
        <img
          src={listing.images[0]}
          alt={listing.title}
          className="w-full h-48 object-cover"
          loading="eager"
        />

        {/* Bookmark button */}
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onBookmarkToggle(listing.id);
          }}
          className="absolute top-2 right-2 p-1.5 bg-white/90 backdrop-blur-sm rounded-full shadow-md hover:bg-white transition-colors"
          aria-label={isBookmarked ? 'Remove Bookmark' : 'Add Bookmark'}
        >
          {isBookmarked ? (
            <BookmarkSolidIcon className="w-5 h-5 text-blue-500" />
          ) : (
            <BookmarkOutlineIcon className="w-5 h-5 text-gray-700" />
          )}
        </button>

        {/* Top viewed badge */}
        {isTopViewed && (
          <div className="absolute top-2 left-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white px-2 py-0.5 rounded-full text-xs font-semibold flex items-center shadow-md">
            <StarIcon className="w-3 h-3 mr-1" />
            <span>Popular</span>
          </div>
        )}

        {/* Photo count badge */}
        {listing.images.length > 1 && (
          <div className="absolute bottom-2 right-2 bg-black/60 backdrop-blur-sm text-white px-2 py-0.5 rounded text-xs font-medium">
            1/{listing.images.length}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-3">
        <div className="flex items-center gap-1 mb-1">
          <MapPin className="w-4 h-4 text-gray-500 flex-shrink-0" />
          <span className="text-sm font-semibold text-gray-900 leading-none truncate">
            {capitalizeWords(listing.location)}
          </span>
        </div>

        {/* Property type and bedrooms */}
        <p className="text-xs text-gray-600 mb-1 truncate">
          {listing.type}
          {listing.bedrooms > 0 && (
            <>
              {' · '}
              {listing.bedrooms} {listing.bedrooms === 1 ? 'bed' : 'beds'}
            </>
          )}
          {listing.bathrooms > 0 && (
            <>
              {' · '}
              {listing.bathrooms} {listing.bathrooms === 1 ? 'bath' : 'baths'}
            </>
          )}
        </p>

        {/* Price */}
        <div className="flex items-baseline mt-1.5">
          <span className="text-base font-bold text-gray-900">€{formatPrice(listing.price)}</span>
          {listing.listingIntent === 'rent' && (
            <span className="text-xs text-gray-600 ml-1">/month</span>
          )}
        </div>
      </div>
    </Link>
  );
};

export default CompactPropertyCard;
